import { API_GATEWAY_URL } from './apiGateway';
import { getStoredToken, getStoredUser } from './authStorage';

export type AdLockScope = 'adsModeration' | 'adsExpertModeration';

export interface AdLock {
    scope: AdLockScope;
    adId: string;
    userId: string;
    login: string;
    acquiredAt: string;
    updatedAt: string;
}

const LOCK_CLIENT_ID_KEY = 'adLockClientId';

function buildHeaders(): HeadersInit {
    const token = getStoredToken();
    return {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {})
    };
}

function toLockScopeValue(scope: AdLockScope): string {
    return encodeURIComponent(scope);
}

async function requestJson<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
    const response = await fetch(`${API_GATEWAY_URL}${endpoint}`, {
        ...options,
        headers: {
            ...buildHeaders(),
            ...(options.headers || {})
        }
    });

    if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Lock API error: ${response.status} ${response.statusText} ${errorText}`);
    }

    return response.json() as Promise<T>;
}

function generateClientId() {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
        return crypto.randomUUID();
    }
    return `lock-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

export function getAdLockClientId(): string {
    if (typeof window === 'undefined') {
        return 'server';
    }

    let current = window.sessionStorage.getItem(LOCK_CLIENT_ID_KEY);
    if (!current) {
        current = generateClientId();
        window.sessionStorage.setItem(LOCK_CLIENT_ID_KEY, current);
    }
    return current;
}

export function getCurrentUserIdentity() {
    const user = (getStoredUser() as { id?: string; login?: string } | null) || null;
    return {
        userId: (user?.id || '').trim(),
        login: (user?.login || '').trim()
    };
}

export function isOwnedByCurrentUser(lock: AdLock | null): boolean {
    if (!lock) {
        return false;
    }
    const identity = getCurrentUserIdentity();
    if (identity.userId && lock.userId) {
        return identity.userId === lock.userId;
    }
    return Boolean(identity.login && lock.login && identity.login === lock.login);
}

export const adLocksApi = {
    async getOne(scope: AdLockScope, adId: string): Promise<AdLock | null> {
        if (!adId) {
            return null;
        }
        const data = await requestJson<{ lock: AdLock | null }>(
            `/ad-locks/${toLockScopeValue(scope)}/${encodeURIComponent(adId)}`
        );
        return data.lock || null;
    },

    async getMany(scope: AdLockScope, adIds: string[]): Promise<Record<string, AdLock | null>> {
        const cleanIds = adIds.map((id) => id.trim()).filter(Boolean);
        if (!cleanIds.length) {
            return {};
        }
        const query = encodeURIComponent(cleanIds.join(','));
        const data = await requestJson<{ locks: Record<string, AdLock | null> }>(
            `/ad-locks/${toLockScopeValue(scope)}?adIds=${query}`
        );
        return data.locks || {};
    },

    async acquire(scope: AdLockScope, adId: string, clientId: string): Promise<{ acquired: boolean; lock: AdLock | null }> {
        const data = await requestJson<{ acquired: boolean; lock: AdLock | null }>(
            `/ad-locks/${toLockScopeValue(scope)}/${encodeURIComponent(adId)}/acquire`,
            {
                method: 'POST',
                body: JSON.stringify({ clientId })
            }
        );
        return data;
    },

    async release(
        scope: AdLockScope,
        adId: string,
        clientId: string,
        options: { keepalive?: boolean; force?: boolean } = {}
    ): Promise<void> {
        const { keepalive = false, force = false } = options;
        await requestJson<{ released: boolean; lock: AdLock | null }>(
            `/ad-locks/${toLockScopeValue(scope)}/${encodeURIComponent(adId)}/release`,
            {
                method: 'POST',
                keepalive,
                body: JSON.stringify({ clientId, force })
            }
        );
    }
};
